import React, { forwardRef, useEffect, useMemo, useState, useRef, useContext } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { mdiAlphabetical, mdiCalendarOutline, mdiSortAscending, mdiSortDescending, mdiViewGrid, mdiFormatListBulleted } from '@mdi/js';
import { Icon } from '@mdi/react';
import { Add, DashboardCustomizeOutlined, DeleteOutline, GroupOutlined } from '@mui/icons-material';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import Constants from 'relevant-shared/reportData/constants';
import { userSummary, uiAdjustedUserObject } from 'relevant-shared/misc/userUtils';
import useReports from '../../hooks/useBreakpoint/useReports';
import { FuzzyFilterBar } from '../../pages/AdminPublisherList/FuzzyFilterBar';
import { useUsers } from '../../stores/userQuery';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/IconButton';
import { Select } from '../Select/Select';
import { Tooltip } from '../Tooltip';
import { isPinned, usePinReport } from '../ViewEditReport/hooks/usePinReport';
import { ReportCard } from './ReportCard';
import { ReportsOverviewStoreDefaults, useReportsOverviewStore } from './reportsOverviewStore';
import { getReportPreviewImage, getReportTypeIcon } from './utils';
import { TopBar } from '../TopBar/TopBar';
import { TabBar } from '../Tabs/TabBar';
import { TabView } from '../Tabs/TabView';
import { Accordion } from '../Accordion/Accordion';
import { stores } from '../../stores';
import { useSidebarStore } from '../../stores/SidebarStore';
import { ReactComponent as KidStarOutline } from '../../assets/icons/kid_star_outline.svg?component';
import { Reports } from '../../api/relevant';
import { useMergedRefs } from '../../hooks/useMergedRefs';
import { NewReportDrawer } from './NewReportDrawer';
import { PageTitle } from '../PageTitle/PageTitle';
import { useGridColumnCount } from '../../hooks/useGridColumnCount';
import { SimpleTable, TableRowType } from '../SimpleTable';
import { DateBadge } from './DateBadge';
import { UserAvatar } from '../Avatar';
import { PinButton } from '../ViewEditReport/PinButton';
import { MenuItem, MoreMenu } from '../MoreMenu/MoreMenu';
import { useCopyToDashboard } from '../ViewEditReport/hooks/useCopyToDashboard';
import useDeleteReport from '../ViewEditReport/hooks/useDeleteReport';
import { SegmentedSelect } from '../SegmentedSelect';
import Analyze from '../../assets/graphics/Analyze-bro.png';
import NewIdeaBro from '../../assets/graphics/New_idea-bro.png';
import EmptyBro from '../../assets/graphics/Empty-bro.png';
import { LearnMoreButton } from '../Smarts/LearnMoreButton';

const sortOptions = [
	{ value: 'created', label: 'Created', icon: <Icon path={mdiCalendarOutline} /> },
	// { value: 'modified', label: 'Modified', icon: <Icon path={mdiCalendarEditOutline} /> },
	{ value: 'az', label: 'Title', icon: <Icon path={mdiAlphabetical} /> },
];

const getAuthor = (report, users) => {
	const info = report.creatorUserInfo;
	return info && uiAdjustedUserObject(
		users.find((u) => u.id === info.id) || userSummary(info),
		stores.identity.isSuperAdministrator(),
	);
};

interface ReportsViewProps {
	reports: Reports[],
	loading: boolean,
}

const ReportsGrid = forwardRef<HTMLDivElement, ReportsViewProps>(({ reports, loading }, ref) => {
	const { data: users } = useUsers();
	const gridRef = useRef<HTMLDivElement>(null);
	const columnCount = useGridColumnCount(gridRef);
	const [setAutoAnimate] = useAutoAnimate();
	const setRef = useMergedRefs([ref, gridRef, setAutoAnimate]);

	return (
		<div
			ref={setRef}
			className={clsx(
				'grid gap-6',
				'grid-cols-[repeat(auto-fill,minmax(360px,1fr))]',
				'animate-fade-in',
			)}
		>
			{reports.map((report) => (
				<ReportCard
					type={report.settings.type}
					key={report.id}
					title={report.settings.title}
					createdAt={report.createdDate}
					author={getAuthor(report, users)}
					isPublic={report.isPublic}
					subscriptions={report.subscriptions}
					link={`${Constants[report.settings.type].reportLocation}/${report.id}`}
					image={getReportPreviewImage(
						report.settings.showPieChart,
						report.settings.showTable,
						report.settings.showChart,
						report.settings.groupBy,
					)}
					report={report}
				/>
			))}
			{loading && (
				<>
					{Array.from({ length: columnCount }).map((_, i) => (
						// eslint-disable-next-line react/no-array-index-key
						<ReportCard skeleton key={`skeleton-${i}`} />
					))}
				</>
			)}
		</div>
	);
});

const ReportsList = forwardRef<HTMLDivElement, ReportsViewProps>(({ reports, loading }, ref) => {
	const { data: users } = useUsers();
	const rows: TableRowType[] = reports.map((report) => {
		const { id, settings: { title, type }, createdDate } = report;
		const author = getAuthor(report, users);
		return {
			id,
			cols: [
				<div className="flex pr-4 items-center gap-2.5 flex-[1_0_0]">
					<img src={getReportTypeIcon(type)} alt={Constants[type].DESCRIPTION} className="size-5" />
					<span className="text-sm text-text-primary font-normal">{title}</span>
				</div>,
				<DateBadge report={report} />,
				author && (
					<div className="flex items-center gap-2 flex-[1_0_0]">
						<UserAvatar
							userId={author.id}
							fallbackUser={author}
							className="min-w-5! min-h-5! m-[2px]"
						/>
						{author?.fullname}
					</div>
				),
				createdDate && moment.utc(createdDate).format('LL'),
				<ReportRowActions report={report} />,
			],
			to: `${Constants[report.settings.type].reportLocation}/${report.id}`,
		};
	});
	return (
		<div className="animate-fade-in" ref={ref}>
			<SimpleTable
				headers={['Title', 'Date Range', 'Author', 'Created', '']}
				rows={loading ? undefined : rows}
				skeletonRows={3}
				columnClass={['w-150 min-w-30!', 'min-w-35!', 'min-w-35!', 'min-w-22!', 'flex w-29! min-w-29! justify-end']}
				thinRows
			/>
		</div>
	);
});

function ReportRowActions({ report }: { report: Reports }) {
	const { pinned, setPinned } = usePinReport({ report });
	const { copyToDashboard } = useCopyToDashboard({ report });
	const deleteReport = useDeleteReport({ reportId: report?.id, report });
	// Need to preventDefault clicks for SimpleTable
	const withPreventDefault = (fn: () => void) => (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		fn();
	};
	return (
		<div className="flex items-center gap-2">
			{report.isPublic && (
				<div className="flex pr-1.5 items-center gap-2">
					<Tooltip title="Public">
						<GroupOutlined className="text-fg-tertiary size-[20px]!" />
					</Tooltip>
				</div>
			)}
			<PinButton size="s" variant="favourite" active={pinned} onToggle={setPinned} />
			<MoreMenu size="s">
				<MenuItem icon={<DashboardCustomizeOutlined />} onClick={withPreventDefault(copyToDashboard)}>Copy to Dashboard</MenuItem>
				<MenuItem icon={<DeleteOutline />} onClick={withPreventDefault(deleteReport)} className="hover:text-fg-highlight">Delete</MenuItem>
			</MoreMenu>
		</div>
	);
}

const viewModes = [
	{ id: 'grid', icon: <Icon path={mdiViewGrid} size="20px" />, component: ReportsGrid },
	{ id: 'list', icon: <Icon path={mdiFormatListBulleted} size="20px" />, component: ReportsList },
];

export function ReportsOverview() {
	const {
		tabIndex,
		reportType,
		sortBy,
		direction,
		viewMode,
		isChanged,
		setTabIndex,
		setReportType,
		setSortBy,
		setDirection,
		setViewMode,
	} = useReportsOverviewStore();
	const reportsOverviewFavouritesCollapsed = useSidebarStore((s) => s.reportsOverviewFavouritesCollapsed);
	const setReportsOverviewFavouritesCollapsed = useSidebarStore((s) => s.setReportsOverviewFavouritesCollapsed);
	const location = useLocation<{ new?: boolean }>();
	const [filterIds, setFilterIds] = useState<string[] | undefined>(undefined);
	const [newReportOpen, setNewReportOpen] = useState(location.state?.new);
	const [searchValue, setSearchValue] = useState(new URLSearchParams(location.search).get('f') ?? '');

	const { reports, availableReportTypes, loading } = useReports();
	const { data: users } = useUsers();
	const initialLoading = loading && !reports;
	const ReportView = viewModes.find((v) => v.id === viewMode).component;

	const reportTypes = useMemo(() => availableReportTypes.filter((t) => !t.noSaveType), [availableReportTypes]);
	const reportTypeOptions = useMemo(() => [
		{ value: 'all', label: 'All types', icon: null },
		...reportTypes.map((t) => ({
			value: t.type,
			label: t.DESCRIPTION,
			icon: <img src={getReportTypeIcon(t.type)} alt={t.DESCRIPTION} />,
		})),
	], [reportTypes]);

	useEffect(() => {
		// Apply filters from query params
		const query = new URLSearchParams(location.search);
		if (query.has('tab')) {
			switch (query.get('tab')) {
				case 'my':
					setTabIndex(1);
					break;
				case 'shared':
					setTabIndex(2);
					break;
				default:
					setTabIndex(0);
					break;
			}
		}
		if (query.has('type') && reportTypeOptions.find((o) => o.value === query.get('type'))) {
			setReportType(query.get('type') as string);
		}
		if (query.has('sort') && sortOptions.find((o) => o.value === query.get('sort'))) {
			setSortBy(query.get('sort') as string);
		}
		if (query.has('direction') && ['asc', 'desc'].includes(query.get('direction') as string)) {
			setDirection(query.get('direction') as 'asc' | 'desc');
		}
	}, []);

	useEffect(() => {
		if (location.state?.new) {
			setNewReportOpen(true);
		}
	}, [location]);

	const allReports = useMemo(() => {
		return reports ? reportTypes.map((t) => reports[t.type] ?? []).flat() : [];
	}, [reports, reportTypes]);

	const filterData = useMemo(() => allReports.map((r) => ({
		id: r.id,
		text: r.settings.title,
		author: getAuthor(r, users)?.fullname,
		data: r,
	})), [allReports, users]);

	const filteredReports = useMemo(() => {
		return allReports
			.filter((r) => reportType === 'all' || r.settings.type === reportType)
			.filter((r) => filterIds?.includes(r.id) ?? true)
			.sort((a, b) => {
				if (sortBy === 'created') {
					if (!a.createdDate) return Infinity;
					if (!b.createdDate) return -Infinity;
					return direction === 'asc' ? new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime() : new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
				} else if (sortBy === 'az') {
					return direction === 'asc' ? a.settings.title.localeCompare(b.settings.title) : b.settings.title.localeCompare(a.settings.title);
				} else {
					return 0;
				}
			});
	}, [allReports, reportType, sortBy, direction, filterIds]);

	const favouriteReports = useMemo(() => filteredReports.filter((r) => isPinned(r)), [filteredReports]);
	const isMine = (r) => !r.isPublic || r.creatorUserInfo?.id === stores.identity.userId();
	const myReports = filteredReports.filter(isMine);
	const sharedWithMeReports = filteredReports.filter((report) => !isMine(report));

	const handleSortByChange = (value: string) => {
		if (sortBy === value) {
			setDirection(direction === 'asc' ? 'desc' : 'asc');
		} else {
			setSortBy(value);
			setDirection('asc');
		}
	};

	const isFiltering = useMemo(() => searchValue !== '' || reportType !== ReportsOverviewStoreDefaults.reportType, [searchValue, reportType]);
	const clearFilters = () => {
		setSearchValue('');
		setReportType(ReportsOverviewStoreDefaults.reportType);
	};

	return (
		<>
			<TopBar
				smartHelp={{
					query: 'reports',
					question: 'Explain how to create a new report and the different report types ARI, HB, HB Historical, Forecast, etc.',
				}}
			/>

			<div className="h-full w-full mx-auto">
				<div className="flex flex-row gap-4 justify-between mb-6 ml-6">
					<PageTitle className="mb-0!">Reports</PageTitle>

					<div className="flex items-start gap-2.5">
						{/* Search */}
						<FuzzyFilterBar
							placeholder="Find a report by title, author"
							value={searchValue}
							data={filterData}
							keys={['text', 'author']}
							onFilter={({ results, value }) => {
								setFilterIds(results?.map((r) => r.item.id));
								setSearchValue(value);
							}}
							shortcut="$mod+F"
							className="w-[300px]"
						/>
						<SegmentedSelect
							items={viewModes}
							selected={[viewMode]}
							onChange={([newMode]) => {
								setViewMode(newMode);
							}}
							noEmptySelected
						/>
					</div>
				</div>

				<div className="flex flex-wrap gap-4 mb-4">
					<TabBar
						tabs={['All reports', 'My reports', 'Shared with me']}
						index={tabIndex}
						onChange={(index) => { setTabIndex(index); setNewReportOpen(false); }}
						className="grow"
					/>

					<div className="flex flex-wrap justify-end gap-2 ml-auto">
						<Select
							value={reportType}
							onChange={setReportType}
							options={reportTypeOptions}
							highlighted={isChanged('reportType')}
						/>

						<Select
							value={sortBy}
							onChange={handleSortByChange}
							options={sortOptions}
							icon={(
								<Tooltip title={direction === 'asc' ? 'Ascending' : 'Descending'}>
									<IconButton
										size="s"
										onClick={(e) => { e.stopPropagation(); handleSortByChange(sortBy); }}
										icon={<Icon path={direction === 'asc' ? mdiSortAscending : mdiSortDescending} className="w-5 h-5" />}
									/>
								</Tooltip>
							)}
							iconIsButton
							highlighted={isChanged('sortBy') || isChanged('direction')}
						/>

						<Button onClick={() => setNewReportOpen(!newReportOpen)} active={newReportOpen}>
							<Add /> New report
						</Button>
					</div>
				</div>

				<NewReportDrawer open={newReportOpen} onClose={() => setNewReportOpen(false)} className="mb-4" />

				{/* Reports View */}
				<TabView index={tabIndex} dismount className="min-h-[400px]">
					{/* All reports */}
					<div>
						{(filteredReports.length > 0 || initialLoading) && (
							<div className="flex flex-col gap-4 animate-fade-in [animation-duration:0.1s]">
								<Accordion
									title="Favourites"
									open={!reportsOverviewFavouritesCollapsed}
									onOpenChange={(nextOpen) => setReportsOverviewFavouritesCollapsed(!nextOpen)}
									dismount
								>
									{(favouriteReports.length > 0 || initialLoading) && (
										<ReportView reports={favouriteReports} loading={initialLoading} />
									)}

									{favouriteReports.length === 0 && !initialLoading && (
										<div className="flex flex-col gap-2 items-center animate-fade-in">
											<span className={clsx('text-sm text-text-tertiary inline-flex items-center', isFiltering && 'invisible')}>Press the star <KidStarOutline className="size-4 text-text-secondary mx-1" /> to add a report to your favourites</span>
										</div>
									)}
								</Accordion>

								<Accordion title="All reports" open dismount>
									<ReportView reports={filteredReports} loading={initialLoading} />

									{filteredReports.length === 0 && !loading && (
										<div className="flex flex-col gap-2 items-center animate-fade-in">
											<span className="text-text-secondary">No reports found</span>
										</div>
									)}
								</Accordion>
							</div>
						)}

						{filteredReports.length === 0 && !loading && (
							<>
								{isFiltering ? (
									<NoResultsBanner clearFilters={clearFilters} />
								) : (
									<GettingStartedBanner />
								)}
							</>
						)}
					</div>

					{/* My reports */}
					<div>
						{(myReports.length > 0 || initialLoading) && (
							<div className="animate-fade-in [animation-duration:0.1s]">
								<ReportView reports={myReports} loading={initialLoading} />
							</div>
						)}

						{myReports.length === 0 && !loading && (
							<>
								{isFiltering ? (
									<NoResultsBanner clearFilters={clearFilters} />
								) : (
									<GettingStartedBanner />
								)}
							</>
						)}
					</div>

					{/* Shared with me */}
					<div>
						{(sharedWithMeReports.length > 0 || initialLoading) && (
							<div className="animate-fade-in [animation-duration:0.1s]">
								<ReportView reports={sharedWithMeReports} loading={initialLoading} />
							</div>
						)}

						{sharedWithMeReports.length === 0 && !loading && (
							<>
								{isFiltering ? (
									<NoResultsBanner clearFilters={clearFilters} />
								) : (
									<div className="flex flex-col items-center animate-fade-in p-6 border-2 rounded-xl border-borders-secondary bg-white">
										<img src={EmptyBro} alt="Analyze" className="size-[200px] mb-3" />
										<span className="text-text-primary text-xl font-bold mb-4">Nothing shared yet</span>
										<p className="text-text-secondary text-sm font-normal mb-2 text-center">Reports directly shared with you, or marked as <span className="font-bold"><GroupOutlined className="text-fg-secondary size-4! -mt-0.5" /> Public</span> by members of your organization will appear here.</p>
										<LearnMoreButton variant="ghost" help={{ query: 'reports', question: 'Explain how reports can be shared' }}>Learn more about shared reports</LearnMoreButton>
									</div>
								)}
							</>
						)}
					</div>
				</TabView>
			</div>
		</>
	);
}

function GettingStartedBanner() {
	return (
		<div className="flex flex-col items-center animate-fade-in p-6 border-2 rounded-xl border-borders-secondary bg-white">
			<img src={NewIdeaBro} alt="New idea" className="size-[200px] mb-3" />
			<span className="text-text-primary text-xl font-bold mb-4">Getting started with Relevant Yield</span>
			<p className="text-text-secondary text-sm font-normal mb-2 text-center">It looks like you don&apos;t have any reports yet!<br />Choose a template to get started analyzing your data and gaining insights into your advertising performance.</p>
			<LearnMoreButton size="s" variant="ghost" help={{ query: 'reports', question: 'Give a short tutorial on how to get started with reporting. Highlight that templates are available and a good starting point.' }}>Learn more about reporting</LearnMoreButton>
		</div>
	);
}

function NoResultsBanner({ clearFilters }: { clearFilters: () => void }) {
	return (
		<div className="flex flex-col items-center animate-fade-in p-6">
			<img src={Analyze} alt="Analyze" className="size-[150px] mb-8" />
			<span className="text-text-primary text-xl font-bold mb-4">Your filter produced no results</span>
			<span className="text-text-secondary text-sm font-normal mb-2 text-center">To widen your search, change or remove filters above.</span>
			<Button size="s" variant="ghost" onClick={clearFilters}>Clear all filters</Button>
		</div>
	);
}
